import { createContext, useContext, type ReactNode } from "react";
import type { ApiClientConfig } from "./client-config.js";

/**
 * The shell injects an {@link ApiClientConfig} (base URL + auth getters) via
 * this context so every module feature package (`solve-feature`,
 * `hoshin-feature`, …) reads the same shape without importing the shell's auth
 * store — or each other.
 *
 * Graduated here from `solve-feature` (PB-220): once a second module
 * (`hoshin-feature`) needed it, keeping the provider + hook in `solve-feature`
 * would force a module→sibling import (the ADR-0009 anti-pattern). Living in
 * `@playbookai/api-clients` makes it ONE shared context object, so the shell's
 * single `ApiClientProvider` serves every module. `solve-feature/src/api/context`
 * re-exports these, so its existing import sites keep resolving unchanged.
 */
// Exported for this package's own optional readers (`useModuleAvailability`);
// the package index does not re-export it.
export const ApiClientContext = createContext<ApiClientConfig | null>(null);

export interface ApiClientProviderProps {
  readonly value: ApiClientConfig;
  readonly children: ReactNode;
}

export function ApiClientProvider({
  value,
  children,
}: ApiClientProviderProps): JSX.Element {
  return (
    <ApiClientContext.Provider value={value}>
      {children}
    </ApiClientContext.Provider>
  );
}

export function useApiClient(): ApiClientConfig {
  const ctx = useContext(ApiClientContext);
  if (!ctx) {
    throw new Error(
      "useApiClient: ApiClientProvider missing. The host shell must wrap the module routers in ApiClientProvider.",
    );
  }
  return ctx;
}
